import type { IUser } from '@/models/users';
import type { SmsOriginNumberResponse } from '@/utils/validation';
import {
  AuthServiceApiFactory,
  UserServiceApiFactory,
  type AuthServiceApiAuthServiceLoginRequest,
  type ModelApiKeyResponse,
  type ModelSessionExpiryResponse,
  type ModelTerminalApiKeyResponse,
  type ModelUserResponse,
} from '@layup/api-sdk';
import { createLayupClient } from './layupApi';

export const authServiceClient = () => {
  const client = createLayupClient();
  return AuthServiceApiFactory(undefined, undefined, client);
};

export const userServiceClient = () => {
  const client = createLayupClient();
  return UserServiceApiFactory(undefined, undefined, client);
};

export const signIn = async (
  username: string,
  password: string,
  rememberMe: boolean,
  turnstileToken: string,
): Promise<ModelUserResponse> => {
  if (!turnstileToken) {
    throw new Error('Turnstile token required. Unable to Sign In.');
  }
  const request: AuthServiceApiAuthServiceLoginRequest = {
    modelLoginRequest: {
      username: username,
      password: password,
      rememberMe: rememberMe,
      token: turnstileToken,
    },
  };
  const response = await authServiceClient().authServiceLogin(request);
  return response.data;
};

export const fetchSmsOriginNumbers = async (): Promise<SmsOriginNumberResponse[]> => {
  const client = createLayupClient(false, false);
  const response = await client.get('v1/sms-origin-numbers');
  return response.data?.smsOriginNumbers ?? response.data;
};

export const signUp = async (mobileNumber: string, password: string, turnstileToken: string): Promise<any> => {
  if (!turnstileToken) {
    throw new Error('Turnstile token required. Unable to sign up.');
  }
  const client = createLayupClient(false, false);
  const response = await client.post('v1/auth/register', { cellNumber: mobileNumber, password, token: turnstileToken });
  return response.data;
};

export const signUpMerchant = async (user: IUser, turnstileToken: string): Promise<any> => {
  const client = createLayupClient(false, false);
  const response = await client.post('v1/auth/register', { ...user, token: turnstileToken });
  return response.data;
};

export const getErrorMessage = (error: any): string => {
  const { response } = error;
  if (response && response.data) {
    const errorData = response.data;
    if (typeof errorData === 'string') {
      if (errorData.includes('not unique') || errorData.includes('Duplicate')) {
        if (errorData.includes('email')) {
          return 'Email already registered. Please sign-in or reset your password.';
        }
        return 'Mobile number already registered. Please sign-in or reset your password.';
      } else if (errorData === 'Invalid cellNumber') {
        return 'Invalid cell number. Please check your number and try again.';
      }
    }
  }
  return 'Something went wrong. Please try again.';
};

export const otpVerification = async (otp: string, channel: 'MOBILE' | 'EMAIL', channelId: string) => {
  if (!otp) throw new Error('OTP is required');
  if (!channelId) {
    if (channel === 'EMAIL') {
      throw new Error('Email is required for verification.');
    } else {
      throw new Error('Cellphone number is required for verification.');
    }
  }
  const client = createLayupClient();
  let queryString = `/v1/verify?token=${otp}`;
  if (channel === 'MOBILE') {
    queryString = `${queryString}&msisdn=${encodeURIComponent(channelId)}`;
  }
  if (channel === 'EMAIL') {
    queryString = `${queryString}&email=${encodeURIComponent(channelId)}`;
  }
  await client.get(queryString);
  const user = await getMe();
  return user;
};

export const resendTwoFactorAuthenticationVerification = async (user: IUser): Promise<any> => {
  const client = createLayupClient();
  const response = await client.post('v1/resend-merchant-user-otp', { ...user });
  return response.data;
};

export const sendEmailVerification = async (data: any): Promise<any> => {
  const client = createLayupClient();
  const response = await client.post('v1/auth/email-verification', data);
  return response.data;
};

export const getMe = async (): Promise<ModelUserResponse> => {
  const client = createLayupClient();
  const response = await client.get('v1/auth/me');
  return response.data;
};

export const forgotPassword = async (
  redirectTo: string,
  channelId: string,
  channel: 'EMAIL' | 'MOBILE',
  turnstileToken: string,
  isRealtimeVerification = false,
) => {
  if (!turnstileToken) {
    throw new Error('Turnstile token required. Unable to reset password.');
  }
  if (!channelId) {
    if (channel === 'MOBILE') {
      throw new Error('Mobile number required. Unable to reset password.');
    } else {
      throw new Error('Email is required. Unable to reset password.');
    }
  }
  const client = createLayupClient();
  const response = await client.post('v1/request-password-reset', {
    redirectTo: redirectTo,
    value: channelId.toLowerCase(),
    channel: channel,
    isRealtimeVerification,
    token: turnstileToken,
  });
  return response.data;
};

export const generateTerminalKey = async (): Promise<ModelTerminalApiKeyResponse> => {
  const response = await authServiceClient().authServiceGenerateTerminalApiKey();
  return response.data;
};

export const generateApiKey = async (): Promise<ModelApiKeyResponse> => {
  const response = await authServiceClient().authServiceGenerateApiKey();
  return response.data;
};

export const signOut = async (): Promise<any> => {
  const client = createLayupClient();
  const response = await client.get('v1/auth/logout');
  return response.data;
};

export const getSessionExpiry = async (): Promise<ModelSessionExpiryResponse> => {
  try {
    const client = createLayupClient();
    const response = await client.get('v1/auth/session-expiry');
    return response.data;
  } catch (err) {
    throw new Error(`Failed to retrieve session expiry: ${err instanceof Error ? err.message : String(err)}`);
  }
};
